'use client';

import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authedFetch } from '@/lib/authed-fetch';

import { IMPERSONATION_STORAGE_KEY as STORAGE_KEY } from '@/lib/auth-cache';

interface ImpersonationState {
  active: boolean;
  sessionId: string | null;
  targetUserId: string | null;
  targetEmail: string | null;
  targetName: string | null;
}

interface ImpersonationContextValue extends ImpersonationState {
  startImpersonation: (targetUserId: string, reason?: string, redirectTo?: string) => Promise<void>;
  stopImpersonation: () => Promise<void>;
}

const defaultState: ImpersonationState = {
  active: false,
  sessionId: null,
  targetUserId: null,
  targetEmail: null,
  targetName: null,
};

const ImpersonationContext = createContext<ImpersonationContextValue>({
  ...defaultState,
  startImpersonation: async () => {},
  stopImpersonation: async () => {},
});

export function useImpersonation() {
  return useContext(ImpersonationContext);
}

export function ImpersonationProvider({ children }: { children: ReactNode }) {
  const [state, setState] = useState<ImpersonationState>(defaultState);

  // Hydrate from sessionStorage on mount
  useEffect(() => {
    try {
      const stored = sessionStorage.getItem(STORAGE_KEY);
      if (stored) {
        const parsed = JSON.parse(stored) as ImpersonationState;
        if (parsed.active && parsed.targetUserId) {
          setState(parsed);
        }
      }
    } catch {
      // Ignore parse errors
    }
  }, []);

  // Persist to sessionStorage on state change
  useEffect(() => {
    if (state.active) {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
    } else {
      sessionStorage.removeItem(STORAGE_KEY);
    }
  }, [state]);

  // Global fetch interceptor: inject X-Impersonate-User header on all requests
  useEffect(() => {
    if (!state.active || !state.targetUserId) return;

    const originalFetch = window.fetch;
    const targetUserId = state.targetUserId;

    window.fetch = function (input: RequestInfo | URL, init?: RequestInit) {
      // Only intercept same-origin API requests
      const url = typeof input === 'string' ? input : input instanceof URL ? input.href : input.url;
      const isSameOrigin = url.startsWith('/') || url.startsWith(window.location.origin);

      if (isSameOrigin) {
        const headers = new Headers(init?.headers);
        if (!headers.has('X-Impersonate-User')) {
          headers.set('X-Impersonate-User', targetUserId);
        }
        return originalFetch(input, { ...init, headers });
      }

      return originalFetch(input, init);
    };

    return () => {
      window.fetch = originalFetch;
    };
  }, [state.active, state.targetUserId]);

  const startImpersonation = useCallback(
    async (targetUserId: string, reason?: string, redirectTo?: string) => {
      const res = await authedFetch(`/api/admin/impersonate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ targetUserId, reason }),
      });

      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Failed to start impersonation' }));
        throw new Error(err.error || 'Failed to start impersonation');
      }

      const data = await res.json();
      const newState: ImpersonationState = {
        active: true,
        sessionId: data.session.id,
        targetUserId: data.session.targetUser.id,
        targetEmail: data.session.targetUser.email,
        targetName: data.session.targetUser.fullName,
      };
      setState(newState);

      // Persist synchronously BEFORE navigating: the persist effect runs after the
      // React commit, which may not flush before window.location.href tears the page
      // down — the reloaded page would then hydrate with no impersonation state and
      // silently drop the X-Impersonate-User header. Mirrors stopImpersonation's
      // synchronous sessionStorage.removeItem below.
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(newState));

      // Navigate to the target user's org (or fallback to /organizations)
      window.location.href = redirectTo || '/';
    },
    []
  );

  const stopImpersonation = useCallback(async () => {
    if (state.sessionId) {
      await authedFetch(`/api/admin/impersonate/stop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ sessionId: state.sessionId }),
      }).catch(() => {
        // Best-effort — still clear local state
      });
    }

    setState(defaultState);
    sessionStorage.removeItem(STORAGE_KEY);

    // Navigate to admin page so we're not stuck on the target's org
    window.location.href = '/admin/impersonate';
  }, [state.sessionId]);

  return (
    <ImpersonationContext.Provider value={{ ...state, startImpersonation, stopImpersonation }}>
      {children}
    </ImpersonationContext.Provider>
  );
}
